/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { ApolloProvider } from '@apollo/client';
import { useNextMiApolloClient, getDeployedEnvType } from '@marriott/mi-apollo-client-utils';
import { Modal, PortalStyleContext } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import axios from 'axios';
import { useGetBreakpoint } from '@marriott/mi-ui-library-shop';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { getBreakpoint } from '../../modules/utils/helpers';
import { getItemFromPageModel, getMockPageModel } from '../../utils/src/helper';
import { respGridUtil } from '@marriott/mi-headless-utils';
import { useConditionalSearchFilterContextWithSerpFlag } from '../../hooks/lib/useConditionalSearchFilterContext';
import { MAP_VIEW_SESSION_KEY } from '../../modules/store/store.constants';

const { IS_LOCAL_DEV } = process.env;

/** Dedicated portal mount for HQV — `standard` (non-branded) root outside ADF `brand-css` trees */
const HQV_PORTAL_ROOT_ID = 'hqv-portal-root';
const HQV_PORTAL_CLASS = 'standard';

const HQV_PORTAL_STYLE_CONTEXT = { portalClassName: HQV_PORTAL_CLASS };

/** Brand CSS sets subtitle font vars on `.brand-css`; HQV must pin global typography tokens on the portal root */
const HQV_TYPOGRAPHY_CSS_VARS = [
  '--t-font-family',
  '--t-title-font-family',
  '--t-subtitle-xl-font-family',
  '--t-subtitle-l-font-family',
  '--t-subtitle-m-font-family',
  '--t-subtitle-s-font-family',
] as const;

const applyHqvGlobalTypography = (portalRoot: HTMLElement) => {
  HQV_TYPOGRAPHY_CSS_VARS.forEach(cssVar => portalRoot.style.setProperty(cssVar, 'var(--t-base-font-family)'));
};

export interface PageonModalProps {
  templateURL?: string;
  modalOpenState?: boolean;
  modelReqBody?: any;
  hotelQuickViewCacheConfig?: any;
  closeModal?: any;
  operationSignatures?: any;
  enableApolloUtility?: boolean;
  aemPath?: string;
  itemPath?: string;
  pageName?: string;
  getMockPageModel?: any;
  getItemFromPageModel?: any;
  respGridUtil?: any;
  ErrorHandlerAlert?: any;
  flexLayout?: boolean;
  // Pre-fetched model data from SWR hook (optional for backward compatibility)
  prefetchedModel?: any;
  prefetchedGridModel?: any;
  prefetchedGridProps?: any;
  isPageModelLoading?: boolean;
  disableScrollOnBody?: boolean;
}

export const PageonModal: React.FC<PageonModalProps> = ({
  templateURL,
  modalOpenState,
  modelReqBody,
  hotelQuickViewCacheConfig,
  closeModal,
  operationSignatures,
  enableApolloUtility,
  aemPath,
  itemPath,
  pageName,
  getMockPageModel: getMockPageModelProp,
  getItemFromPageModel: getItemFromPageModelProp,
  respGridUtil: respGridUtilProp,
  ErrorHandlerAlert,
  flexLayout,
  // Pre-fetched model data
  prefetchedModel,
  prefetchedGridModel,
  prefetchedGridProps,
  isPageModelLoading,
  disableScrollOnBody,
}) => {
  const { envVarsObject } = useClientEnvVarsStore();
  const [hqvPortalContainer, setHqvPortalContainer] = useState<HTMLDivElement | null>(null);
  const [modelTemplate, setModelTemplate] = useState<any>({});
  const [responsiveGridModelHQV, setResponsiveGridModelHQV] = useState<any>({});
  const [responsiveGridModelPropsHQV, setResponsiveGridModelPropsHQV] = useState<any>({});

  // Mount a dedicated non-branded portal root on `body` (sibling to `#__next`, never under `brand-css`)
  useLayoutEffect(() => {
    if (!hqvPortalContainer) {
      let portalRoot = document.getElementById(HQV_PORTAL_ROOT_ID) as HTMLDivElement | null;
      if (!portalRoot) {
        portalRoot = document.createElement('div');
        portalRoot.id = HQV_PORTAL_ROOT_ID;
        portalRoot.className = HQV_PORTAL_CLASS;
        document.body.appendChild(portalRoot);
      } else {
        portalRoot.className = HQV_PORTAL_CLASS;
      }
      applyHqvGlobalTypography(portalRoot);
      setHqvPortalContainer(portalRoot);
    }
  }, [hqvPortalContainer]);

  // Determine if we're using the pre-fetch pattern
  // If isPageModelLoading is passed (even as true/false), we're using pre-fetch and should NOT do fallback fetch
  const usePrefetchPattern = isPageModelLoading !== undefined;

  /**
   * useEffect -> It checks for local development and decides to use mock data or api call for AEM model
   * sets AEM modal(mock or API response) on responsiveGridModelHQV variable based
   */
  useEffect(() => {
    // If using pre-fetch pattern, wait for data from parent (don't do fallback fetch)
    if (usePrefetchPattern) {
      if (prefetchedModel) {
        setModelTemplate(prefetchedModel);
        setResponsiveGridModelHQV(prefetchedGridModel || {});
        setResponsiveGridModelPropsHQV(prefetchedGridProps || {});
      }
      return;
    }

    // Backward compatibility: fetch on mount if no pre-fetched data
    // Use prop functions if provided, otherwise use imported functions
    const getMockPageModelFn = getMockPageModelProp || getMockPageModel;
    const getItemFromPageModelFn = getItemFromPageModelProp || getItemFromPageModel;
    const respGridUtilFn = respGridUtilProp || respGridUtil;

    /**
     * making an Page modal call on component load based on modelReqBody value
     * sets AEM modal on responsiveGridModelHQV variable based on AEM response
     * @param reqBody: Request body for hybrid config app in order to provide model
     * @param url: hybrid config app URL from where we get AEM model
     */
    const getPageModel = async (reqBody: any, url: string) => {
      const requestId = envVarsObject?.['REQUEST_ID'];
      let pageModel = {};
      try {
        const response = await axios.post(`${url}`, reqBody, {
          headers: {
            'x-request-id': requestId,
            'accept-language': reqBody?.localeKey || 'en-US',
          },
        });
        if (response) {
          pageModel = response?.data?.data;
        } else {
          pageModel = {};
        }
      } catch (err) {
        return pageModel;
      }
      return pageModel;
    };

    (async function () {
      let template: any;
      if (IS_LOCAL_DEV === 'true') {
        template = await getMockPageModelFn(pageName);
      } else {
        template = await getPageModel(modelReqBody, templateURL as string);
      }
      setModelTemplate(template);
      const gridModel = getItemFromPageModelFn(template, itemPath);
      setResponsiveGridModelHQV(gridModel);
      setResponsiveGridModelPropsHQV(respGridUtilFn(gridModel));
    })();
  }, [usePrefetchPattern, prefetchedModel, prefetchedGridModel, prefetchedGridProps]);

  useEffect(() => {
    function handleResize() {
      setIsmobilePort(getBreakpoint() === 'mobile');
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const deployedEnvType = getDeployedEnvType();
  const HQVMiApolloClient = useNextMiApolloClient(operationSignatures, deployedEnvType, hotelQuickViewCacheConfig);
  const [isMobilePort, setIsmobilePort] = useState(useGetBreakpoint() === 'mobile');

  // Get filterModalOpen from Zustand store or React Context based on feature flag
  const { filterModalOpen } = useConditionalSearchFilterContextWithSerpFlag();

  // Get isMapViewEnabled from sessionStorage
  const isMapViewEnabled =
    typeof sessionStorage !== 'undefined' && sessionStorage.getItem(MAP_VIEW_SESSION_KEY) === 'true';

  const shouldDisableScrollOnBody = disableScrollOnBody ?? (!modalOpenState && !(filterModalOpen || isMapViewEnabled));

  return (
    <PortalStyleContext.Provider value={HQV_PORTAL_STYLE_CONTEXT}>
      <ErrorHandlerAlert alertMessage={modelTemplate?.['componentErrorMessage']}>
        {modalOpenState && hqvPortalContainer && (
          <Modal
            className={`custom-scrollbar  ${modalOpenState ? 'hqv-modal-main-class' : 'close-popup-header'}`}
            secondaryClassName={HQV_PORTAL_CLASS}
            show={true}
            handleBlur={true}
            popupOpenState={modalOpenState as boolean}
            setPopupOpenState={closeModal}
            allFilterPopUp={true}
            onClose={closeModal}
            modalId={'hqv-modal'}
            role="dialog"
            labelledBy="hqv-modal"
            disableScrollOnBody={shouldDisableScrollOnBody}
            flexLayout={flexLayout}
            usePortal={true}
            portalContainer={hqvPortalContainer}
          >
            <Modal.Header
              className={clsx('hqv-modal-content-header')}
              labelText={' '}
              popupHeaderOnCLoseFunc={closeModal}
              flexLayout={flexLayout}
            />
            <Modal.Body
              className={clsx(HQV_PORTAL_CLASS, 'custom-scrollbar', 'hqv-modal-content', 'modal-content-loader')}
              flexLayout={flexLayout}
            >
              <div className={clsx('m-spinner-m')}></div>
            </Modal.Body>
            <Modal.Body
              className={clsx(
                HQV_PORTAL_CLASS,
                isMobilePort ? '' : 'custom-scrollbar',
                'hqv-modal-content',
                'hqv-modal-data',
                'hqv-modal-component-data'
              )}
              flexLayout={flexLayout}
            >
              <div className={HQV_PORTAL_CLASS}>
                {enableApolloUtility ? (
                  <ApolloProvider client={HQVMiApolloClient}>
                    <ResponsiveGrid
                      {...responsiveGridModelPropsHQV}
                      model={responsiveGridModelHQV}
                      pagePath={aemPath}
                      itemPath={itemPath}
                    />
                  </ApolloProvider>
                ) : (
                  <ResponsiveGrid
                    {...responsiveGridModelPropsHQV}
                    model={responsiveGridModelHQV}
                    pagePath={aemPath}
                    itemPath={itemPath}
                  />
                )}
              </div>
            </Modal.Body>
          </Modal>
        )}
      </ErrorHandlerAlert>
    </PortalStyleContext.Provider>
  );
};
